import { ReactComponent as AddIcon } from 'assets/icons/add24.svg'
import { dismissToastMessages } from 'common/ToastMessage'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { observer } from 'mobx-react-lite'
import { forwardRef, useState, useImperativeHandle } from 'react'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { Workflow } from 'stores/Workflows/Workflow'
import { templates, TemplateType } from 'components/FolderTemplatesDialog/Templates'
import { workflowFromTemplate } from '../utils'
import Styles from './CreateForm.module.scss'
import { List } from '../List'
import { useCreateWorkflow } from 'hooks/queries/workflow/useCreateWorkflow'
import { IWorkflow, StatePropertyValue } from 'stores/Workflows/Types'
import { Property } from 'api/clients/PropertyClient'
import { WorkflowBuilderHandle } from '../UpdateForm/Types'
import { Intro } from '../Intro'
import { TemplateMenu } from '../TemplateMenu/TemplateMenu'
import { TemplatesGrid } from './TemplatesGrid'
import { useConfirmUnsavedChanges } from '../useConfirmUnsavedChanges'
import { WorkflowTemplate } from 'components/FolderTemplatesDialog/Types'
import { useInheritableTemplate } from '../useInheritableTemplate'
import { Modal } from 'almanac-ui/Modal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

interface Props {
  folderId: string
  onCreate: () => void
  onClose?: () => void
}

const CreateForm = observer(
  forwardRef<WorkflowBuilderHandle, Props>(({ folderId, onCreate, onClose }, fRef) => {
    const [templateId, setTemplateId] = useState<TemplateType>()
    const [workflow, setWorkflow] = useState<Workflow>()
    const [errors, setErrors] = useState<string[]>([])
    const { mutate, isLoading: isSaving } = useCreateWorkflow({ folderId })
    const confirmUnsavedChanges = useConfirmUnsavedChanges()
    const inheritableTemplate = useInheritableTemplate({ folderId })
    const { isMobile } = useResponsiveDesign()

    useImperativeHandle(
      fRef,
      () => ({
        confirmUnsavedChanges: async () => {
          if (!workflow) {
            return true
          }
          return confirmUnsavedChanges()
        },
        isDirty: () => !!workflow,
      }),
      [workflow, confirmUnsavedChanges],
    )

    const reset = () => {
      setTemplateId(undefined)
      setWorkflow(undefined)
    }

    const handleSelectTemplate = (template: WorkflowTemplate) => {
      setTemplateId(template.id)
      setWorkflow(
        Workflow.fromJSON(
          workflowFromTemplate({
            template,
            workflowData: {
              users: [],
              channels: [],
              configuration: {},
              folder: { key: folderId } as IWorkflow['folder'], // TODO: make underlying types smaller
              property: { property_values: template.statuses.property_values } as Property<StatePropertyValue>,
            },
          }),
        ),
      )
    }

    const save = async () => {
      if (!workflow) {
        return
      }
      setErrors([])
      const errors = workflow.validate()
      if (errors.length > 0) {
        showErrorMessageToast(errors[0])
        return
      }

      const { configuration, property_values } = workflow.toJSON()
      showToastMessage('Creating workflow...', 'loading')
      mutate(
        {
          property_values: property_values,
          config: configuration,
        },
        {
          onSuccess: () => {
            dismissToastMessages()
            showToastMessage('Workflow created', 'success')
            onCreate()
          },
          onError() {
            dismissToastMessages()
            showToastMessage('An error occurred, our team has been notified.', 'error')
          },
        },
      )
    }

    const createState = () => {
      if (!workflow) {
        return
      }
      workflow.createState({}).setFocus(true)
    }

    if (workflow && templateId) {
      return (
        <>
          {isMobile && (
            <Modal.Header
              title="Statuses and Automation"
              onClose={onClose}
              actions={
                <TemplateMenu
                  inheritableTemplate={inheritableTemplate}
                  selectedKey={templateId}
                  onSelectionChange={handleSelectTemplate}
                />
              }
            />
          )}
          <Modal.Content>
            <Intro />

            <div className={Styles.section}>
              {!isMobile && (
                <TemplateMenu
                  inheritableTemplate={inheritableTemplate}
                  selectedKey={templateId}
                  onSelectionChange={handleSelectTemplate}
                />
              )}
              <List workflow={workflow} />
              {errors.length > 0 && <p className={Styles.errors}>{errors.join(',')}</p>}

              <Modal.FooterButton frontIcon={AddIcon} variant="secondary" size="lg" onPress={createState}>
                New Status
              </Modal.FooterButton>
            </div>
          </Modal.Content>
          <Modal.Footer>
            <Modal.FooterContent slot="left">
              <Modal.FooterButton size="lg" variant="tertiary" onPress={reset} isDisabled={isSaving}>
                Cancel
              </Modal.FooterButton>
            </Modal.FooterContent>
            <Modal.FooterContent slot="right">
              <Modal.FooterButton size="lg" onPress={save} isDisabled={isSaving}>
                Save
              </Modal.FooterButton>
            </Modal.FooterContent>
          </Modal.Footer>
        </>
      )
    }

    return (
      <>
        {isMobile && <Modal.Header title="Statuses and Automation" onClose={onClose} />}
        <Modal.Content>
          <Intro />
          <div className={Styles.section}>
            <h3 className={Styles.sectionTitle}>Start from a template...</h3>
            <TemplatesGrid onSelect={handleSelectTemplate} />
          </div>
        </Modal.Content>

        <Modal.Footer>
          <Modal.FooterContent slot="left">
            {inheritableTemplate && (
              <Modal.FooterButton
                variant="tertiary"
                size="lg"
                onPress={() => handleSelectTemplate(inheritableTemplate)}
              >
                Copy from Parent Folder
              </Modal.FooterButton>
            )}
          </Modal.FooterContent>
          <Modal.FooterContent slot="right">
            <Modal.FooterButton
              size="lg"
              onPress={() => {
                const defaultTemplate = templates.find(({ id }) => id === TemplateType.Default)
                defaultTemplate && handleSelectTemplate(defaultTemplate)
              }}
            >
              Create New Workflow
            </Modal.FooterButton>
          </Modal.FooterContent>
        </Modal.Footer>
      </>
    )
  }),
)

export { CreateForm }
